import { useCallback, useEffect, useState } from 'react';
import { useListContext, useNotify, usePermissions } from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useFeature } from '../featureFlags/FeatureGate';
import { defaultDepartmentFor, departmentListParams } from '../departmentView';
import {
  FEATURE_BANK_DEFAULT_VIEWS,
  FEATURE_BANK_FILTERS,
  FEATURE_BANK_SAVED_VIEWS,
  PLAIN_LIST_PARAMS,
  roleDefaultListParams,
  type BankListParams,
} from './bankFtl';
import { deleteView, fetchSavedViews, saveView, type SavedView } from './savedViewsApi';
import { toErrorMessage } from './orderActionsApi';

/**
 * „Widoki” nad listą banku (makieta FTL pkt 9.3, 15.10.2026):
 *
 * - **„Widok domyślny”** (przełącznik `bankRoleDefaultViews`, razem
 *   z filtrami z makiety) — wraca do widoku, od którego rola zaczyna
 *   (`roleDefaultListParams`); bez widoku roli — widok działu albo lista bez
 *   filtra, jak przy pierwszym wejściu.
 * - **Zapisane widoki** (przełącznik `bankSavedViews`) — filtry i sortowanie
 *   pod nazwą, prywatne, zapisane na koncie (`saved-list-views/` w API):
 *   wybór, zapis bieżącego, usunięcie.
 *
 * Widok „mojej floty” ma stały filtr „moje” (`MyFleetBank.tsx`), więc jego
 * widok domyślny to lista bez innych filtrów.
 */

/** Widok, do którego wraca „Widok domyślny”. */
export function defaultViewFor(role: string | null | undefined, myFleet: boolean): BankListParams {
  if (myFleet) return PLAIN_LIST_PARAMS;
  const roleView = roleDefaultListParams(role);
  if (roleView) return roleView;
  const department = defaultDepartmentFor(role);
  return department ? departmentListParams(department) : PLAIN_LIST_PARAMS;
}

export const BankViewsMenu = ({ myFleet = false }: { myFleet?: boolean }) => {
  const savedOn = useFeature(FEATURE_BANK_SAVED_VIEWS);
  const defaultViewsOn = useFeature(FEATURE_BANK_DEFAULT_VIEWS);
  // Widok roli niesie nowe klucze filtrów — bez nich API by je pominęło.
  const filtersOn = useFeature(FEATURE_BANK_FILTERS);
  const defaultOn = defaultViewsOn && filtersOn;
  const { filterValues, displayedFilters, setFilters, sort, setSort } = useListContext();
  const { permissions } = usePermissions<string>();
  const notify = useNotify();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [views, setViews] = useState<SavedView[]>([]);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  const load = useCallback(async () => {
    try {
      setViews(await fetchSavedViews());
    } catch {
      // Lista widoków to dodatek — bez niej menu ma „Widok domyślny” i zapis.
      setViews([]);
    }
  }, []);

  useEffect(() => {
    if (savedOn) void load();
  }, [savedOn, load]);

  if (!savedOn && !defaultOn) return null;

  const apply = (params: BankListParams | SavedView['params']) => {
    setFilters(params.filter ?? {}, params.displayedFilters ?? {}, false);
    if (params.sort) setSort({ field: params.sort, order: params.order ?? 'ASC' });
    setAnchor(null);
  };

  const save = async () => {
    try {
      const saved = await saveView(name, {
        filter: filterValues ?? {},
        displayedFilters: (displayedFilters ?? {}) as Record<string, boolean>,
        sort: sort?.field,
        order: sort?.order as 'ASC' | 'DESC' | undefined,
      });
      notify(`Zapisano widok „${saved.name}”`, { type: 'success' });
      setNaming(false);
      setName('');
      await load();
    } catch (err) {
      notify(toErrorMessage(err), { type: 'error' });
    }
  };

  const remove = async (view: SavedView) => {
    try {
      await deleteView(view.id);
      notify(`Usunięto widok „${view.name}”`, { type: 'info' });
      await load();
    } catch (err) {
      notify(toErrorMessage(err), { type: 'error' });
    }
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<BookmarksOutlinedIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
      >
        Widoki
      </Button>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {defaultOn ? (
          <MenuItem onClick={() => apply(defaultViewFor(permissions, myFleet))}>
            <ListItemText
              primary="Widok domyślny"
              secondary={myFleet ? 'bez dodatkowych filtrów' : 'od tego widoku zaczyna Twoja rola'}
            />
          </MenuItem>
        ) : null}
        {savedOn && views.length > 0 ? <Divider /> : null}
        {savedOn
          ? views.map((v) => (
              <MenuItem key={v.id} onClick={() => apply(v.params)}>
                <ListItemText primary={v.name} />
                <Tooltip title={`Usuń widok „${v.name}”`}>
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label={`Usuń widok „${v.name}”`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void remove(v);
                    }}
                  >
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MenuItem>
            ))
          : null}
        {savedOn ? <Divider /> : null}
        {savedOn ? (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setNaming(true);
            }}
          >
            Zapisz bieżący widok…
          </MenuItem>
        ) : null}
      </Menu>
      <Dialog open={naming} onClose={() => setNaming(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Zapisz widok listy</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Nazwa widoku"
            value={name}
            onChange={(e) => setName(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 60 } }}
            helperText="Filtry i sortowanie listy. Widok jest prywatny; ta sama nazwa zastępuje zapisany widok."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNaming(false)}>Anuluj</Button>
          <Button variant="contained" disabled={name.trim() === ''} onClick={() => void save()}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
